import type { CritiqueDimensionId, CritiqueScore } from '@/lib/concept-workbench/ad-critique';

/**
 * How the six model-judged dimensions are grouped and banded on the rail.
 * Pure on purpose: the grouping and the band cut-offs are rules, so they live
 * in code with a test rather than in JSX.
 */

export type JudgmentGroupId = 'creative' | 'message';

export const JUDGMENT_GROUPS: ReadonlyArray<{
  id: JudgmentGroupId;
  label: string;
  dimensions: readonly CritiqueDimensionId[];
}> = [
  {
    id: 'creative',
    label: 'Creative',
    dimensions: ['scroll_stop', 'legibility', 'brand_fit'],
  },
  {
    id: 'message',
    label: 'Message',
    dimensions: ['offer_clarity', 'proof_strength', 'audience_fit'],
  },
];

export type ScoreBand = 'low' | 'mid' | 'high';

/** 1 to 4 low, 5 to 7 mid, 8 to 10 high. A band is a reading aid, never a gate. */
export function scoreBand(score: number): ScoreBand {
  if (score <= 4) return 'low';
  if (score <= 7) return 'mid';
  return 'high';
}

export interface JudgmentGroup {
  id: JudgmentGroupId;
  label: string;
  scores: CritiqueScore[];
}

export function groupScores(scores: readonly CritiqueScore[]): JudgmentGroup[] {
  return JUDGMENT_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    scores: group.dimensions
      .map((dimension) => scores.find((score) => score.dimension === dimension))
      .filter((score): score is CritiqueScore => Boolean(score)),
  })).filter((group) => group.scores.length > 0);
}

/** The lowest one or two dimensions, only those that are not already high. */
export function biggestGaps(scores: readonly CritiqueScore[], limit = 2): CritiqueScore[] {
  return scores
    .map((score, index) => ({ score, index }))
    .filter(({ score }) => scoreBand(score.score) !== 'high')
    .sort((a, b) => a.score.score - b.score.score || a.index - b.index)
    .slice(0, limit)
    .map(({ score }) => score);
}
